const MAX_EDGE = 1600;
const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED = new Set(["image/jpeg", "image/png", "image/webp"]);

export function mediaPath(photoKey: string): string {
  return `/media/${photoKey.replace(/^\/+/, "")}`;
}

export function mediaUrl(photoKey: string): string {
  const origin = (import.meta.env.VITE_SITE_URL as string | undefined)?.replace(/\/+$/, "") || "";
  const base = origin || (typeof location !== "undefined" ? location.origin : "");
  return `${base}${mediaPath(photoKey)}`;
}

function extType(file: File): string {
  if (ALLOWED.has(file.type)) return file.type;
  return "image/jpeg";
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read that image."));
    };
    img.src = url;
  });
}

export async function downscalePhoto(file: File): Promise<{ blob: Blob; contentType: string }> {
  const contentType = extType(file);
  const img = await loadImage(file);
  const longEdge = Math.max(img.width, img.height);
  const scale = longEdge > MAX_EDGE ? MAX_EDGE / longEdge : 1;
  const width = Math.max(1, Math.round(img.width * scale));
  const height = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not process that image.");
  ctx.drawImage(img, 0, 0, width, height);
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, contentType, 0.85),
  );
  if (!blob) throw new Error("Could not process that image.");
  if (blob.size > MAX_BYTES) {
    throw new Error("That photo is still larger than 10 MB after shrinking.");
  }
  return { blob, contentType };
}

export async function preparePhoto(file: File): Promise<{ blob: Blob; contentType: string }> {
  if (file.size <= MAX_BYTES && ALLOWED.has(file.type)) {
    const img = await loadImage(file);
    if (Math.max(img.width, img.height) <= MAX_EDGE) {
      return { blob: file, contentType: file.type };
    }
  }
  return downscalePhoto(file);
}
